import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Trash2, Upload, Download, MoreVertical, Pencil, X } from "lucide-react";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import Select from "react-select";
import { payrollOpsService, salaryStructureService } from "../../../services/api";
import EmpCodeNameSearchBar from "../../Common/EmpCodeNameSearchBar";
import { filterRowsByEmpCodeOrName } from "../../../lib/empCodeNameSearch";

function formatPayMonthLabel(payMonth) {
  const s = String(payMonth || "").slice(0, 7);
  const m = s.match(/^(\d{4})-(\d{2})$/);
  if (!m) return s || "—";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return d.toLocaleString("en-IN", { month: "long", year: "numeric" });
}

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

/**
 * Add / list monthly variables (recurrent salary components) for scoped employees.
 */
export default function PayrollOneTimeInputsPanel({
  darkMode = false,
  payMonth,
  employees = [],
  employeeLookup = [],
  onChanged,
  readOnly = false,
  canImport = null,
}) {
  const importAllowed = canImport == null ? !readOnly : !!canImport;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recurrentComponents, setRecurrentComponents] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [form, setForm] = useState({
    inputType: "earning",
    componentName: "",
    amount: "",
    payoutPercent: "100",
    remarks: "",
  });
  const [search, setSearch] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [showInputModal, setShowInputModal] = useState(false);
  const fileRef = useRef(null);
  const menuRef = useRef(null);

  const loadComponents = useCallback(async () => {
    try {
      const res = await payrollOpsService.listRecurrentComponents();
      setRecurrentComponents(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setRecurrentComponents([]);
    }
  }, []);

  const load = useCallback(async () => {
    if (!payMonth) return;
    setLoading(true);
    try {
      const res = await payrollOpsService.listMonthlyInputs(payMonth);
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [payMonth]);

  useEffect(() => {
    load();
    loadComponents();
  }, [load, loadComponents]);

  useEffect(() => {
    if (openMenuId == null) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openMenuId]);

  const componentOptions = useMemo(() => {
    const type = form.inputType === "deduction" ? "deduction" : "earning";
    return (recurrentComponents || []).filter((c) => (c.inputType || "earning") === type);
  }, [recurrentComponents, form.inputType]);

  const empById = useMemo(() => {
    const map = new Map();
    [...(employeeLookup || []), ...(employees || [])].forEach((e) => {
      if (e?.id == null) return;
      map.set(Number(e.id), e);
    });
    return map;
  }, [employees, employeeLookup]);

  const employeeSelectOptions = useMemo(
    () =>
      (employees || []).map((e) => ({
        value: e.id,
        label: `${e.empCode || e.id} — ${e.name || ""}`.trim(),
      })),
    [employees]
  );

  const selectStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        minHeight: 34,
        fontSize: 12,
        backgroundColor: darkMode ? "#2C2C2C" : "#fff",
        borderColor: darkMode ? "#4C4C5C" : "#d1d5db",
        boxShadow: "none",
      }),
      menu: (base) => ({
        ...base,
        zIndex: 40,
        backgroundColor: darkMode ? "#2C2C2C" : "#fff",
        fontSize: 12,
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: darkMode ? "#3a3a3a" : "#fee2e2",
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: darkMode ? "#f3f4f6" : "#991b1b",
        fontSize: 11,
      }),
      input: (base) => ({ ...base, color: darkMode ? "#fff" : "#111" }),
      singleValue: (base) => ({ ...base, color: darkMode ? "#fff" : "#111" }),
      placeholder: (base) => ({ ...base, color: darkMode ? "#9ca3af" : "#6b7280" }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused
          ? darkMode
            ? "#3a3a3a"
            : "#f3f4f6"
          : darkMode
            ? "#2C2C2C"
            : "#fff",
        color: darkMode ? "#fff" : "#111",
      }),
    }),
    [darkMode]
  );

  const downloadTemplate = async () => {
    try {
      if (!recurrentComponents.length) {
        toast.error("No recurrent components found in salary structures");
        return;
      }
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Monthly Variables");
      const listSheet = wb.addWorksheet("Components");

      recurrentComponents.forEach((c, i) => {
        listSheet.getCell(i + 1, 1).value = c.name;
      });
      listSheet.state = "veryHidden";

      const headers = [
        "Sl No",
        "Employee Code",
        "Name",
        "Month",
        "Monthly Variable",
        "Amount",
      ];
      ws.addRow(headers);
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE8E8E8" },
        };
      });

      const monthLabel = formatPayMonthLabel(payMonth);
      ws.addRow([1, "", "", monthLabel, "", ""]);

      ws.columns = [
        { width: 8 },
        { width: 16 },
        { width: 28 },
        { width: 16 },
        { width: 24 },
        { width: 14 },
      ];

      const lastListRow = Math.max(1, recurrentComponents.length);
      ws.dataValidations.add("E2:E2000", {
        type: "list",
        allowBlank: false,
        formulae: [`Components!$A$1:$A$${lastListRow}`],
        showErrorMessage: true,
        errorTitle: "Invalid Monthly Variable",
        error: "Select a recurrent component from the dropdown",
        showInputMessage: true,
        promptTitle: "Monthly Variable",
        prompt: "Choose from recurrent components",
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `monthly-variables-${payMonth}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel template downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to build template");
    }
  };

  const parseExcelRows = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const sheetName =
      workbook.SheetNames.find((n) => /monthly/i.test(n)) || workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const json = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
    return (json || []).map((raw) => {
      const mapped = {};
      Object.entries(raw).forEach(([k, v]) => {
        mapped[normalizeHeader(k)] = v;
      });
      return {
        empCode:
          mapped.employeecode ||
          mapped.empcode ||
          mapped.code ||
          "",
        name: mapped.name || mapped.employeename || "",
        month: mapped.month || mapped.paymonth || payMonth,
        componentName:
          mapped.monthlyvariable ||
          mapped.component ||
          mapped.componentname ||
          mapped.variable ||
          "",
        amount: mapped.amount,
        remarks: mapped.remarks || null,
      };
    });
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xls") && !lower.endsWith(".xlsx")) {
      toast.error("Only Excel files (.xls / .xlsx) are allowed");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    try {
      const parsed = await parseExcelRows(file);
      const dataRows = parsed.filter(
        (r) =>
          String(r.empCode || "").trim() ||
          String(r.name || "").trim() ||
          String(r.componentName || "").trim()
      );
      if (!dataRows.length) {
        toast.error("No data rows in file");
        return;
      }
      const res = await payrollOpsService.bulkCreateMonthlyInputs({
        payMonth,
        rows: dataRows,
      });
      if (res.errors?.length) {
        toast.error(`${res.created || 0} imported, ${res.errors.length} error(s)`);
        console.warn("Monthly variables import errors", res.errors);
      } else {
        toast.success(`${res.created} row(s) imported`);
      }
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resetForm = () => {
    setEditingRow(null);
    setSelectedEmployees([]);
    setForm({
      inputType: "earning",
      componentName: "",
      amount: "",
      payoutPercent: "100",
      remarks: "",
    });
  };

  const closeInputModal = () => {
    setShowInputModal(false);
    resetForm();
  };

  const openAddModal = () => {
    resetForm();
    setShowInputModal(true);
  };

  const handleEdit = (row) => {
    const emp = empById.get(Number(row.employeeId));
    setEditingRow({
      ...row,
      empCode: emp?.empCode || emp?.employeeCode || String(row.employeeId),
      name: emp?.name || "—",
    });
    setSelectedEmployees([]);
    setForm({
      inputType: row.inputType === "deduction" ? "deduction" : "earning",
      componentName: row.componentName || "",
      amount: row.amount != null ? String(row.amount) : "",
      payoutPercent: "100",
      remarks: row.remarks || "",
    });
    setOpenMenuId(null);
    setShowInputModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingRow?.id) return;
    if (!form.componentName.trim() || !Number(form.amount)) {
      toast.error("Select Monthly Variable and amount");
      return;
    }
    try {
      await payrollOpsService.updateMonthlyInput(editingRow.id, {
        inputType: form.inputType,
        componentName: form.componentName.trim(),
        amount: Number(form.amount),
        remarks: form.remarks || null,
      });
      toast.success("Updated");
      closeInputModal();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Update failed");
    }
  };

  const handleAdd = async () => {
    if (editingRow) {
      await handleSaveEdit();
      return;
    }
    if (!selectedEmployees.length || !form.componentName.trim() || !Number(form.amount)) {
      toast.error("Select employee(s), Monthly Variable, and amount");
      return;
    }
    const pct = Number(form.payoutPercent);
    const payoutPct = Number.isFinite(pct) && pct > 0 ? pct : 100;
    const payableAmount = Math.round(Number(form.amount) * (payoutPct / 100) * 100) / 100;
    const ids = selectedEmployees.map((o) => Number(o.value)).filter((id) => id > 0);
    let ok = 0;
    const errors = [];
    for (const employeeId of ids) {
      try {
        await payrollOpsService.createMonthlyInput({
          payMonth,
          employeeId,
          inputType: form.inputType,
          componentName: form.componentName.trim(),
          amount: payableAmount,
          remarks:
            form.remarks ||
            (payoutPct !== 100 ? `Payout ${payoutPct}% of ${form.amount}` : null),
        });
        ok += 1;
      } catch (err) {
        const code = empById.get(employeeId)?.empCode || employeeId;
        errors.push(
          `${code}: ${err?.response?.data?.message || err?.message || "failed"}`
        );
      }
    }
    if (ok) toast.success(`${ok} input(s) added`);
    if (errors.length) toast.error(errors.slice(0, 3).join("; "));
    if (ok) {
      closeInputModal();
      await load();
      onChanged?.();
    }
  };

  const handleDelete = async (id) => {
    setOpenMenuId(null);
    if (editingRow?.id === id) closeInputModal();
    try {
      await payrollOpsService.deleteMonthlyInput(id);
      toast.success("Deleted");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    }
  };

  const inputCls = `rounded border px-2 py-1.5 text-xs w-full ${
    darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-white" : "border-[hsl(var(--ds-border))]"
  }`;

  const displayRows = useMemo(() => {
    const mapped = rows.map((r) => {
      const emp = empById.get(Number(r.employeeId));
      return {
        ...r,
        empCode: emp?.empCode || emp?.employeeCode || String(r.employeeId),
        name: emp?.name || "—",
      };
    });
    return filterRowsByEmpCodeOrName(mapped, search);
  }, [rows, empById, search]);

  const totalMapped = rows.length;

  const inputModal =
    showInputModal &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        className="fixed inset-0 z-[100060] flex items-center justify-center bg-black/50 p-4"
        onClick={closeInputModal}
        role="presentation"
      >
        <div
          className={`w-full max-w-2xl overflow-hidden rounded-xl border shadow-xl ${
            darkMode
              ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-white"
              : "border-[hsl(var(--ds-border))] bg-white text-[hsl(var(--ds-fg))]"
          }`}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="mv-input-modal-title"
        >
          <div
            className={`flex items-center justify-between border-b px-4 py-3 ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <div>
              <h3 id="mv-input-modal-title" className="text-sm font-semibold">
                {editingRow ? "Edit Monthly Variable" : "Add Monthly Variable"}
              </h3>
              <p className="mt-0.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                {formatPayMonthLabel(payMonth)}
                {editingRow
                  ? ` · ${editingRow.empCode} — ${editingRow.name}`
                  : " · select one or more employees"}
              </p>
            </div>
            <button
              type="button"
              onClick={closeInputModal}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="space-y-3 px-4 py-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {!editingRow ? (
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                    Employees
                  </label>
                  <Select
                    isMulti
                    isSearchable
                    isDisabled={readOnly || !employees.length}
                    options={employeeSelectOptions}
                    value={selectedEmployees}
                    onChange={(opts) => setSelectedEmployees(opts || [])}
                    placeholder="Search & select employees (multi)…"
                    styles={{
                      ...selectStyles,
                      menuPortal: (base) => ({ ...base, zIndex: 100070 }),
                    }}
                    classNamePrefix="mv-emp"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              ) : (
                <div
                  className={`sm:col-span-2 rounded-lg border px-3 py-2 text-xs ${
                    darkMode
                      ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                      : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/30"
                  }`}
                >
                  Employee:{" "}
                  <strong>
                    {editingRow.empCode} — {editingRow.name}
                  </strong>
                </div>
              )}

              <div>
                <label className="mb-1 block text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                  Category
                </label>
                <select
                  className={inputCls}
                  value={form.inputType}
                  disabled={readOnly}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      inputType: e.target.value,
                      componentName: "",
                    }))
                  }
                >
                  <option value="earning">Earning</option>
                  <option value="deduction">Deduction</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                  Monthly Variable
                </label>
                <select
                  className={inputCls}
                  value={form.componentName}
                  disabled={readOnly || !componentOptions.length}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, componentName: e.target.value }))
                  }
                >
                  <option value="">
                    {componentOptions.length
                      ? "Select Monthly Variable"
                      : "No monthly variable components"}
                  </option>
                  {componentOptions.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                      {String(c.taxTreatment || "")
                        .toLowerCase()
                        .includes("onetime")
                        ? " (One Time tax)"
                        : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                  Amount
                </label>
                <input
                  className={inputCls}
                  type="number"
                  min="0"
                  placeholder="Amount"
                  value={form.amount}
                  disabled={readOnly}
                  onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                />
                {!readOnly &&
                  !editingRow &&
                  selectedEmployees.length === 1 &&
                  /performance\s*bonus|\bpb\b|bonus/i.test(form.componentName || "") && (
                    <button
                      type="button"
                      className="mt-1 text-[11px] font-medium text-[hsl(var(--ds-primary))] hover:underline"
                      onClick={async () => {
                        const empId = selectedEmployees[0]?.id || selectedEmployees[0]?.value;
                        if (!empId || !payMonth) return;
                        try {
                          const res = await salaryStructureService.getPerformanceBonusAccrual(
                            empId,
                            {
                              payMonth,
                              payoutPercent: form.payoutPercent || 100,
                            }
                          );
                          const data = res?.data || res;
                          if (data?.accruedTotal != null) {
                            setForm((p) => ({
                              ...p,
                              amount: String(data.accruedTotal),
                              remarks:
                                p.remarks ||
                                `PB accrual ${data.frequency || ""} (attendance-prorated); apply payout % on save`,
                            }));
                            toast.success(
                              `Suggested accrued base ₹${Number(data.accruedTotal).toLocaleString("en-IN")} (then × payout %)`
                            );
                          } else {
                            toast.error("No PB accrual available for this employee");
                          }
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.message ||
                              err?.message ||
                              "Could not load PB accrual"
                          );
                        }
                      }}
                    >
                      Suggest from attendance accrual (DB)
                    </button>
                  )}
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                  Payout %
                </label>
                <input
                  className={inputCls}
                  type="number"
                  min="0"
                  step="1"
                  title="Payout % (e.g. 80 or 120). Applied to Amount when adding."
                  placeholder="Payout %"
                  value={form.payoutPercent}
                  disabled={readOnly || !!editingRow}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, payoutPercent: e.target.value }))
                  }
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                  Remarks (optional)
                </label>
                <input
                  className={inputCls}
                  placeholder="Remarks (optional)"
                  value={form.remarks}
                  disabled={readOnly}
                  onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div
            className={`flex flex-wrap items-center justify-end gap-2 border-t px-4 py-3 ${
              darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
            }`}
          >
            <button
              type="button"
              onClick={closeInputModal}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
                  : "border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
              }`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={
                readOnly ||
                (!editingRow && (!employees.length || !componentOptions.length))
              }
              className="inline-flex items-center justify-center gap-1 rounded-lg bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {editingRow ? (
                "Save changes"
              ) : (
                <>
                  <Plus size={14} /> Add input
                </>
              )}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="space-y-3">
      {readOnly && (
        <p className={`text-xs ${darkMode ? "text-amber-200" : "text-amber-800"}`}>
          All employees are processed — unprocess someone before adding monthly variables.
        </p>
      )}
      {!readOnly && employees.length === 0 && (
        <p className={`text-xs ${darkMode ? "text-amber-200" : "text-amber-800"}`}>
          No unprocessed employees available. Unprocess employees on the Process step to add
          inputs.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={downloadTemplate}
          disabled={!importAllowed}
          className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs disabled:opacity-50 ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"}`}
        >
          <Download size={12} /> Template
        </button>
        <label
          className={`inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 text-xs ${!importAllowed ? "opacity-50 pointer-events-none" : ""} ${darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"}`}
        >
          <Upload size={12} /> Import Excel
          <input
            ref={fileRef}
            type="file"
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleBulkUpload}
            disabled={!importAllowed}
          />
        </label>
        <button
          type="button"
          onClick={openAddModal}
          disabled={readOnly || !employees.length || !componentOptions.length}
          className="inline-flex items-center justify-center gap-1 rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          <Plus size={14} /> Add Input
        </button>
        {!recurrentComponents.length && (
          <span className={`text-[11px] ${darkMode ? "text-amber-200" : "text-amber-700"}`}>
            No recurrent components in salary structures yet.
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-[hsl(var(--ds-muted-fg))]">Loading inputs…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No monthly variables for this month yet.</p>
      ) : (
        <div className="space-y-2">
          <EmpCodeNameSearchBar
            value={search}
            onChange={setSearch}
            darkMode={darkMode}
            showCount
            total={totalMapped}
            filtered={displayRows.length}
          />
          {displayRows.length === 0 ? (
            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
              No employees match “{search.trim()}”.
            </p>
          ) : (
        <div className="max-h-52 overflow-auto rounded border text-xs">
          <table className="w-full">
            <thead className={darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}>
              <tr>
                <th className="px-2 py-1.5 text-left w-12">Sl No</th>
                <th className="px-2 py-1.5 text-left">Code</th>
                <th className="px-2 py-1.5 text-left">Name</th>
                <th className="px-2 py-1.5 text-left">Type</th>
                <th className="px-2 py-1.5 text-left">Component</th>
                <th className="px-2 py-1.5 text-right">Amount</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r, idx) => (
                <tr key={r.id} className={darkMode ? "border-t border-[#3C3C3C]" : "border-t"}>
                  <td className="px-2 py-1 tabular-nums text-[hsl(var(--ds-muted-fg))]">
                    {idx + 1}
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap">{r.empCode}</td>
                  <td className="px-2 py-1">{r.name}</td>
                  <td className="px-2 py-1 capitalize">{r.inputType}</td>
                  <td className="px-2 py-1">{r.componentName}</td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {Number(r.amount).toLocaleString()}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {!readOnly && (
                      <div
                        className="relative inline-block"
                        ref={openMenuId === r.id ? menuRef : null}
                      >
                        <button
                          type="button"
                          onClick={() => setOpenMenuId(openMenuId === r.id ? null : r.id)}
                          className={`inline-flex h-7 w-7 items-center justify-center rounded hover:bg-[hsl(var(--ds-muted))] ${
                            darkMode ? "text-[hsl(var(--ds-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                          }`}
                          aria-label="Row actions"
                        >
                          <MoreVertical size={14} />
                        </button>
                        {openMenuId === r.id && (
                          <div className="absolute right-0 top-full z-50 mt-0.5 w-32 overflow-hidden rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 shadow-[var(--ds-shadow-card)]">
                            <button
                              type="button"
                              onClick={() => handleEdit(r)}
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-[hsl(var(--ds-muted))]"
                            >
                              <Pencil size={12} />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(r.id)}
                              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-600 hover:bg-[hsl(var(--ds-muted))]"
                            >
                              <Trash2 size={12} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            className={`border-t px-2 py-1.5 text-[11px] font-medium ${darkMode ? "border-[#3C3C3C] text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}
          >
            Records: {displayRows.length}
          </div>
        </div>
          )}
        </div>
      )}

      {inputModal}
    </div>
  );
}
